/**
 * Vyhledávací multiselect placementů (02 FR-1).
 *
 * Nahrazuje čtyři dropdowny (web / zařízení / dodavatel / fáze) jedním polem.
 * Uživatel píše `lifee`, `px`, `refresh`, `fixed`, kus placement_id… (viz
 * lib/placementSearch); seznam je abecedně. „Vybrat vše zobrazené" grabne
 * jedním klikem všechno, co po vyhledání zbylo — jinak by výběr „všeho A" byl
 * klikací peklo.
 *
 * Prázdný výběr = všechny placementy (jako když není filtr).
 * Vybrané `placement_id` řídí volající a ukládá je do URL (deep-link).
 */
import { useMemo, useState } from 'react';
import type { PlacementOption } from '@/api/models';
import { filterPlacements, sortPlacements } from '@/lib/placementSearch';
import { formatInt } from '@/lib/formatCz';

export interface PlacementMultiSelectProps {
  placements: readonly PlacementOption[];
  selected: string[];
  onChange: (ids: string[]) => void;
  isLoading?: boolean;
}

export function PlacementMultiSelect({
  placements,
  selected,
  onChange,
  isLoading = false,
}: PlacementMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const sorted = useMemo(() => sortPlacements(placements), [placements]);
  const visible = useMemo(() => filterPlacements(sorted, query), [sorted, query]);

  const selectedLabel = useMemo(() => {
    if (selected.length === 0) return null;
    if (selected.length === 1) {
      const one = placements.find((p) => p.placement_id === selected[0]);
      return one?.label_cs ?? selected[0];
    }
    return `${selected.length} vybráno`;
  }, [selected, placements]);

  const toggle = (id: string) => {
    onChange(selectedSet.has(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  /** Přidá do výběru všechny právě zobrazené (sjednocení, neruší mimo-filtr výběr). */
  const selectAllVisible = () => {
    const merged = new Set(selected);
    for (const p of visible) merged.add(p.placement_id);
    onChange([...merged]);
  };

  /** Kolik ze zobrazených ještě není vybraných — řídí smysl tlačítka. */
  const unselectedVisible = visible.filter((p) => !selectedSet.has(p.placement_id)).length;

  return (
    <div className="multi-select placement-select">
      <button
        type="button"
        className={`multi-select__trigger${selected.length > 0 ? ' is-active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
          <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.3" fill="none" />
          <path d="M10.5 10.5 14 14" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
        <span className="multi-select__label">Placementy:</span>
        <span className="multi-select__summary">{selectedLabel ?? 'všechny'}</span>
        <svg viewBox="0 0 10 6" width="9" height="6" aria-hidden="true">
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.4" fill="none" />
        </svg>
      </button>

      {open ? (
        <>
          <div className="popover-backdrop" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="placement-select__panel" role="dialog" aria-label="Výběr placementů">
            <div className="placement-select__search">
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Hledat: vlasta, px, r2b2, refresh, fixed…"
                aria-label="Hledat placementy"
                autoFocus
              />
            </div>

            <div className="placement-select__meta">
              <span>
                {isLoading
                  ? 'Načítám…'
                  : `${visible.length} z ${placements.length} placementů`}
              </span>
              <div className="placement-select__meta-actions">
                <button
                  type="button"
                  className="placement-select__action"
                  onClick={selectAllVisible}
                  disabled={unselectedVisible === 0}
                  title="Přidá do výběru všechny právě vyfiltrované placementy"
                >
                  Vybrat vše zobrazené
                </button>
                {selected.length > 0 ? (
                  <button
                    type="button"
                    className="placement-select__action placement-select__action--clear"
                    onClick={() => onChange([])}
                  >
                    Zrušit výběr ({selected.length})
                  </button>
                ) : null}
              </div>
            </div>

            <ul className="placement-select__list" role="listbox" aria-label="Placementy" aria-multiselectable="true">
              {visible.length === 0 ? (
                <li className="placement-select__empty">
                  {isLoading
                    ? 'Načítám placementy…'
                    : 'Nic neodpovídá. Hledá se ve webu, pozici, zařízení, fázi, px / r2b2 i v placement_id.'}
                </li>
              ) : (
                visible.map((p) => {
                  const isSelected = selectedSet.has(p.placement_id);
                  return (
                    <li key={p.placement_id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        className={`placement-select__option${isSelected ? ' is-active' : ''}`}
                        onClick={() => toggle(p.placement_id)}
                      >
                        <span className="placement-select__check" aria-hidden="true">
                          {isSelected ? '✓' : ''}
                        </span>
                        <span className="placement-select__opt-label">{p.label_cs}</span>
                        <span className="placement-select__opt-imp">
                          {formatInt(p.impressions)}
                        </span>
                      </button>
                    </li>
                  );
                })
              )}
            </ul>

            <div className="placement-select__footer">
              <span className="muted">
                Prázdný výběr = všechny placementy. Víc slov se kombinuje (AND).
              </span>
              <button
                type="button"
                className="btn btn--ghost btn--xs"
                onClick={() => setOpen(false)}
              >
                Hotovo
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
